import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { apiClient } from '../../services/api';

interface Indicator {
  id: number;
  indicator_name: string;
  indicator_code?: string;
  evaluation_rules?: string;
  evaluation_analysis_description?: string;
}

interface SingleIndicatorOpinionDialogProps {
  open: boolean;
  onClose: () => void;
  indicator: Indicator | null;
  projectId: number;
  onSuccess?: () => void;
}

export default function SingleIndicatorOpinionDialog({ 
  open, 
  onClose, 
  indicator, 
  projectId,
  onSuccess 
}: SingleIndicatorOpinionDialogProps) {
  const [facts, setFacts] = useState('');
  const [isLoadingFacts, setIsLoadingFacts] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [noRelevantContent, setNoRelevantContent] = useState(false);

  // 当对话框打开时，自动加载指标关联的日志片段
  useEffect(() => {
    if (!open || !indicator || !projectId) {
      return;
    }

    const loadFacts = async () => {
      setIsLoadingFacts(true);
      setError(null);
      setNoRelevantContent(false);
      
      try {
        // 调用预览模式的接口，获取事实内容
        const response = await apiClient.post(
          `/projects/${projectId}/indicators/${indicator.id}/generate-opinion`,
          { save_to_database: false }
        );

        console.log('后端返回的完整响应:', response);
        
        if (response.success && response.data) {
          const { facts, no_relevant_content } = response.data;
          console.log('response.data.facts:', facts);
          console.log('response.data.no_relevant_content:', no_relevant_content);
          
          if (no_relevant_content) {
            // 没有关联日志
            setNoRelevantContent(true);
            setFacts('');
            setError(response.message || '未找到相关日志内容，请手动输入事实依据');
          } else {
            // 有关联日志
            console.log('设置facts内容:', facts);
            setFacts(facts || '');
            setNoRelevantContent(false);
          }
        } else {
          throw new Error(response.message || '加载事实内容失败');
        }
      } catch (err: any) {
        console.error('加载事实内容失败:', err);
        setError(err.message || '加载事实内容失败');
        setFacts('');
      } finally {
        setIsLoadingFacts(false);
      }
    };

    loadFacts();
  }, [open, indicator, projectId]);

  const handleGenerate = async () => {
    if (!indicator || !projectId) return;
    
    if (!facts.trim()) {
      setError('请输入事实依据');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      // 调用保存模式的接口，生成评价意见
      const response = await apiClient.post(
        `/projects/${projectId}/indicators/${indicator.id}/generate-opinion`,
        { 
          save_to_database: true,
          edited_facts: facts
        },
        { timeout: 300000 } // 5分钟超时
      );

      if (response.success) {
        setSuccess('评价意见生成成功！');
        
        // 延迟关闭，让用户看到成功消息
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        throw new Error(response.error || '生成评价意见失败');
      }
    } catch (err: any) {
      console.error('生成评价意见失败:', err);
      setError(err.message || '生成评价意见失败');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (isGenerating) return;
    setFacts('');
    setError(null);
    setSuccess(null);
    setNoRelevantContent(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isGenerating}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">
            生成评价意见 - {indicator?.indicator_name}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {noRelevantContent && (
            <Alert severity="info" sx={{ mb: 2 }}>
              未找到该指标关联的日志片段，请手动输入事实依据
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>
            指标编码：{indicator?.indicator_code || '无'}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            评价规则：
          </Typography>
          <Box 
            sx={{ 
              p: 1.5, 
              bgcolor: 'action.hover', 
              borderRadius: 1,
              mb: 2,
              maxHeight: 120,
              overflowY: 'auto'
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {indicator?.evaluation_rules || '无'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            事实依据：
            {isLoadingFacts && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          <TextField
            multiline
            rows={12}
            fullWidth
            value={facts}
            onChange={(e) => setFacts(e.target.value)}
            placeholder="请输入或编辑事实依据..."
            variant="outlined"
            disabled={isLoadingFacts || isGenerating}
            sx={{ mt: 1 }}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            提示：系统会基于上述事实依据和评价规则，使用AI生成专业的评价意见并自动计算得分
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isGenerating}>
          取消
        </Button>
        <Button 
          onClick={handleGenerate}
          variant="contained"
          disabled={isGenerating || isLoadingFacts || !facts.trim()}
          startIcon={isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
        >
          {isGenerating ? '生成中...' : '生成评价意见'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

